import { useCallback, useEffect, useState } from 'react';
import { fetchBackofficePreferences, upsertBackofficePreference } from '@/preferences';

export interface ArchitectureGraphSettings extends Record<string, unknown> {
  zoomOnClick: boolean;
}

const PREFERENCE_SCOPE = 'observability:architecture';
const PREFERENCE_SCHEMA_VERSION = 1;
const DEFAULT_SETTINGS: ArchitectureGraphSettings = { zoomOnClick: true };

function resolveArchitectureGraphSettings(value: Record<string, unknown> | undefined) {
  return {
    zoomOnClick: typeof value?.zoomOnClick === 'boolean' ? value.zoomOnClick : true,
  } satisfies ArchitectureGraphSettings;
}

export function useArchitectureGraphSettings() {
  const [settings, setSettings] = useState<ArchitectureGraphSettings>(DEFAULT_SETTINGS);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    setLoaded(false);
    setLoadError(null);
    fetchBackofficePreferences([PREFERENCE_SCOPE], controller.signal)
      .then((preferences) => {
        const preference = preferences.find((item) => item.scope === PREFERENCE_SCOPE);
        setSettings(resolveArchitectureGraphSettings(preference?.value));
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setSettings(DEFAULT_SETTINGS);
        setLoadError(
          caught instanceof Error ? caught : new Error('Failed to load graph settings.')
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoaded(true);
      });

    return () => controller.abort();
  }, []);

  const saveSettings = useCallback(async (nextSettings: ArchitectureGraphSettings) => {
    const resolved = resolveArchitectureGraphSettings(nextSettings);
    await upsertBackofficePreference(PREFERENCE_SCOPE, resolved, PREFERENCE_SCHEMA_VERSION);
    setSettings(resolved);
  }, []);

  return { settings, loaded, loadError, saveSettings };
}
